import { PublicClientApplication, AuthenticationResult, UrlString } from '@azure/msal-browser';
import { ClientConfigurationError } from '@azure/msal-common/dist/error/ClientConfigurationError';
import { msalConfig$ } from './configuration';
import { apiUrl$, clientHeaders, configuration, configuration$ } from '../configuration';

const TOKEN_LIFETIME_MS_MARGIN = 5_000;

// The default of the MSAL library must be overwritten because it is not allowed to have a non https identity server
UrlString.prototype.validateAsUri = function () {
    // Attempts to parse url for uri components
    let components;
    try {
        components = this.getUrlComponents();
    } catch (e) {
        throw ClientConfigurationError.createUrlParseError(e);
    }
    // Throw error if URI or path segments are not parseable.
    if (!components.HostNameAndPort || !components.PathSegments) {
        throw ClientConfigurationError.createUrlParseError('Given url string: ' + this.urlString);
    }

    if (!configuration.keycloakUrl.startsWith('http://localhost')) {
        // Throw error if uri is insecure.
        if (!components.Protocol || components.Protocol.toLowerCase() !== 'https:') {
            throw ClientConfigurationError.createInsecureAuthorityUriError(this.urlString);
        }
    }
};

const client$ = (async () => new PublicClientApplication(await msalConfig$))();

export const isStorybook = () => {
    try {
        return window.location.href.includes('localhost:6006') || window.location.href.includes('storybook');
    } catch (e) {
        return false;
    }
};
// Captured at module-eval time — before React Router mounts and its "/" -> "/dashboard"
// redirect strips the "#state=...&code=..." fragment. MSAL's config load is async, so by
// the time handleRedirectPromise runs the URL fragment is already gone; passing the captured
// hash lets MSAL exchange the code instead of looping (no cached account -> loginRedirect ->
// stripped hash -> no_account_error -> repeat).
const initialResponseHash = window.location.hash;

export let authentication$: Promise<AuthenticationResult> = (async () => {
    const client = await client$;
    const redirectAuthResult = await client.handleRedirectPromise(initialResponseHash);
    if (redirectAuthResult !== null) return parseAuth(redirectAuthResult, client);
    try {
        const silentAuthResult = await client.acquireTokenSilent({ scopes: [] });
        if (silentAuthResult) return parseAuth(silentAuthResult, client);
    } catch (err) {
        console.error(err);
    }
    await client.loginRedirect(); // page will reload, code will not continue
})();

function parseAuth(authResult: AuthenticationResult, client: PublicClientApplication) {
    client.setActiveAccount(authResult.account);
    return authResult;
}

export async function getAccessToken(): Promise<string> {
    const client = await client$;
    const auth = await authentication$;
    // check if there is logged-in session, with a valid token
    if (auth !== null) {
        let tokenExpiryMs = auth.expiresOn ? auth.expiresOn.getTime() : 0;
        tokenExpiryMs -= TOKEN_LIFETIME_MS_MARGIN;
        if (new Date().getTime() < tokenExpiryMs) return auth.accessToken;
    }
    try {
        // try to silently refresh the token
        authentication$ = client.acquireTokenSilent({ scopes: [] });
        return (await authentication$).accessToken;
    } catch (error) {
        console.log('failed to retrieve token silently', error);
        // try to fetch a new token through a redirect, this will stop the current request
        await client.acquireTokenRedirect({ scopes: [] });
    }
    return null;
}

// user$ enables the api access tokens
export const user$ = (async () => {
    // Dynamic import for the same bundle-size reason as in ../configuration.
    const [{ GlobalApi, setDefaultConfiguration }, basePath] = await Promise.all([
        import('@phrospr/api-backend'),
        apiUrl$,
    ]);
    await authentication$;
    // enable authentication for the API
    setDefaultConfiguration({
        basePath,
        accessToken: getAccessToken,
        baseOptions: { headers: clientHeaders },
    });
    return (await GlobalApi.getAuth()).data;
})();

export async function logout() {
    const client = await client$;
    const auth = await authentication$;
    await client.logoutRedirect({ idTokenHint: auth.idToken });
}
